import { getFormattedDate } from "@/helpers/date.helper";
import type { ChallengeStageDetails } from "@/services/challenges/types/challenges.types";
import { isPastFirstRealityStage } from "@/utils/registrationProfile";

const POST_ADVANCE_REGIONAL_BRIEFING_PENDING_KEY =
  "sgb-post-advance-regional-briefing-pending";
const POST_ADVANCE_WELCOME_DISMISSED_USER_KEY =
  "sgb-post-advance-welcome-dismissed-user";

export function markPostAdvanceWelcomeDismissed(userId: string): void {
  try {
    sessionStorage.setItem(POST_ADVANCE_WELCOME_DISMISSED_USER_KEY, userId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function isPostAdvanceWelcomeDismissed(userId: string): boolean {
  try {
    return (
      sessionStorage.getItem(POST_ADVANCE_WELCOME_DISMISSED_USER_KEY) === userId
    );
  } catch {
    return false;
  }
}

export function clearPostAdvanceWelcomeDismissed(): void {
  try {
    sessionStorage.removeItem(POST_ADVANCE_WELCOME_DISMISSED_USER_KEY);
  } catch {
    /* ignore */
  }
}

export function markPostAdvanceRegionalBriefingPending(): void {
  try {
    sessionStorage.setItem(POST_ADVANCE_REGIONAL_BRIEFING_PENDING_KEY, "1");
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearPostAdvanceRegionalBriefingPending(): void {
  try {
    sessionStorage.removeItem(POST_ADVANCE_REGIONAL_BRIEFING_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function isPostAdvanceRegionalBriefingPendingAfterSave(): boolean {
  try {
    return (
      sessionStorage.getItem(POST_ADVANCE_REGIONAL_BRIEFING_PENDING_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export type RegionalStagePeriod = {
  start: string;
  end: string;
};

/** Período das provas práticas a partir dos desafios da etapa atual (prioriza presenciais). */
export function resolveRegionalStagePeriod(
  challenges: ChallengeStageDetails[] | undefined,
  stageEndDate?: string
): RegionalStagePeriod | null {
  if (!challenges?.length) {
    if (!stageEndDate) return null;
    return { start: stageEndDate, end: stageEndDate };
  }

  const inPerson = challenges.filter((c) => c.type === "InPersonChallenge");
  const pool = inPerson.length > 0 ? inPerson : challenges;

  const startDates = pool
    .map((c) => c.startDate)
    .filter((d): d is string => Boolean(d?.trim()));
  const endDates = pool
    .map((c) => c.endDate ?? c.scheduledFor ?? c.startDate)
    .filter((d): d is string => Boolean(d?.trim()));

  if (!startDates.length && !endDates.length) {
    if (!stageEndDate) return null;
    return { start: stageEndDate, end: stageEndDate };
  }

  const start = startDates.sort()[0];
  const end = (endDates.length ? endDates.sort().reverse()[0] : start) ?? start;

  return { start, end };
}

export function formatRegionalPeriodLabel(
  period: RegionalStagePeriod | null,
  stageEndDate?: string
): string {
  if (period) {
    const startLabel = getFormattedDate(period.start, true);
    const endLabel = getFormattedDate(period.end, true);
    if (startLabel && endLabel && startLabel !== endLabel) {
      return `${startLabel} a ${endLabel}`;
    }
    return startLabel || endLabel;
  }
  if (stageEndDate) {
    return getFormattedDate(stageEndDate, true);
  }
  return "em breve";
}

export function buildPostAdvanceRegionalBriefingText(): string {
  return (
    `Nossa equipe entrará em contato com você para enviar as orientações sobre [b]transporte e hospedagem para[/b] a participação da etapa.`
  );
}

type RegionalBriefingGateParams = {
  userId?: string;
  isStagesLoading: boolean;
  isAuthenticatedPage: boolean;
  isInscriptionLoading: boolean;
  hasInscription: boolean;
  isUserOutOfReality?: boolean;
  inscriptionBlocksUi: boolean;
  currentStageNumber: number;
  advancedToResultStage?: boolean;
  /** Cadastro complementar salvo no backend (ou flag imediato pós-save). */
  hasPostAdvanceProfileComplete: boolean;
};

/**
 * 2ª parte: cadastro complementar completo — após salvar e em todo login seguinte.
 * Participante bloqueado na UI até clicar em Sair (logout).
 */
export function shouldShowPostAdvanceRegionalBriefing({
  userId,
  isStagesLoading,
  isAuthenticatedPage,
  isInscriptionLoading,
  hasInscription,
  isUserOutOfReality,
  inscriptionBlocksUi,
  currentStageNumber,
  advancedToResultStage = false,
  hasPostAdvanceProfileComplete,
}: RegionalBriefingGateParams): boolean {
  if (!userId || !isAuthenticatedPage) return false;
  if (isInscriptionLoading || !hasInscription) return false;
  if (inscriptionBlocksUi) return false;
  if (isUserOutOfReality === true && !advancedToResultStage) return false;
  if (
    isStagesLoading &&
    !advancedToResultStage &&
    currentStageNumber <= 1
  ) {
    return false;
  }
  if (!isPastFirstRealityStage(currentStageNumber, advancedToResultStage)) {
    return false;
  }
  return (
    hasPostAdvanceProfileComplete ||
    isPostAdvanceRegionalBriefingPendingAfterSave()
  );
}

/** Snapshot do formulário após salvar (antes do refetch do usuário). */
export function isPostAdvanceProfileCompleteFromForm(
  currentStageNumber: number,
  fields: {
    shoeSize?: string;
    shirtSize?: string;
    pantsSize?: string;
    gloveSize?: string;
    address?: string;
    numberAddress?: string;
    neighborhood?: string;
    complement?: string;
    cep?: string;
    addressProofUrl?: string;
    identityDocumentUrl?: string;
    instagramAccount?: string;
    motherName?: string;
    birthPlace?: string;
    maritalStatus?: string;
    rgRne?: string;
    rgIssuingAgency?: string;
    rgIssueDate?: string;
  }
): boolean {
  if (currentStageNumber <= 1) return true;
  return !!(
    fields.shoeSize?.trim() &&
    fields.shirtSize?.trim() &&
    fields.pantsSize?.trim() &&
    fields.gloveSize?.trim() &&
    fields.address?.trim() &&
    fields.numberAddress?.trim() &&
    fields.neighborhood?.trim() &&
    fields.cep?.trim() &&
    fields.addressProofUrl?.trim() &&
    fields.identityDocumentUrl?.trim() &&
    fields.motherName?.trim() &&
    fields.birthPlace?.trim() &&
    fields.maritalStatus?.trim() &&
    fields.rgRne?.trim() &&
    fields.rgIssuingAgency?.trim() &&
    fields.rgIssueDate?.trim()
  );
}
